import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { action, observable } from 'mobx';
import { CELL_TYPES, Search } from 'core/components/table';
import RemoveButton from 'core/form/components/RemoveButton';
import { Box, Button, Checkbox, DropdownMenu, Flex, Menu, MenuItem, Tag, Text } from 'core/components';

import AdminTable from 'app/components/admin/AdminTable';
import GlobalFilterCollection from 'app/stores/globalFilter/GlobalFilterCollection';

@inject('$auth', '$rbac')
@observer
class RoleSetGlobalFilters extends Component {
  state = {
    // Every scope in the company, minus the ones already granted to this role set.
    dropdownScopeCollection: new GlobalFilterCollection()
  };

  @observable
  saving = false;

  componentDidMount() {
    const { model } = this.props;
    const { dropdownScopeCollection } = this.state;

    dropdownScopeCollection.setPresetFilter({ fn: (scope) => !model.scopes.get(scope.id) });

    dropdownScopeCollection.fetch();
  }

  @action
  setSaving = (saving) => {
    this.saving = saving;
  };

  // Re-applies the preset filter on every refetch so newly granted scopes drop out of the picker.
  refetchRoleSetScopes = () => {
    const { model } = this.props;
    const { dropdownScopeCollection } = this.state;

    return model.fetch().then(() => {
      dropdownScopeCollection.setPresetFilter({ fn: (scope) => !model.scopes.get(scope.id) });
    });
  };

  handleAdd = () => {
    const { model, $rbac } = this.props;
    const { dropdownScopeCollection } = this.state;
    const scopeIds = dropdownScopeCollection.selectedArray.map((scope) => scope.id);

    this.setSaving(true);
    return $rbac
      .bulkAddGlobalFiltersToRoleSet(scopeIds, model.id)
      .finally(() => this.setSaving(false))
      .then(() => {
        dropdownScopeCollection.clearSelection();
        return this.refetchRoleSetScopes();
      });
  };

  handleRemove = (scopeId) => {
    const { model, $rbac } = this.props;

    this.setSaving(true);
    return $rbac
      .bulkRemoveGlobalFiltersFromRoleSet([scopeId], model.id)
      .finally(() => this.setSaving(false))
      .then(() => this.refetchRoleSetScopes());
  };

  // Passing the id of the row that is already active clears the default rather than re-setting it.
  handleToggleLoginDefault = (scopeModel) => {
    const { model, $rbac } = this.props;
    const nextScopeId = scopeModel.get('active_on_login') ? null : scopeModel.id;

    if (this.saving) {
      return Promise.resolve();
    }

    this.setSaving(true);
    return $rbac
      .setRoleSetLoginGlobalFilter(nextScopeId, model.id)
      .finally(() => this.setSaving(false))
      .then(() => this.refetchRoleSetScopes());
  };

  get canEdit() {
    const { $auth, viewOnly } = this.props;
    return !viewOnly && $auth.hasRbacPermissions(['rbac.role::update']);
  }

  get renderAddFilterDropdownMenu() {
    const { dropdownScopeCollection } = this.state;

    return (
      <DropdownMenu
        onClose={() => dropdownScopeCollection.clearSelection()}
        content={
          <Box>
            <Search collection={dropdownScopeCollection} />
            <Menu py={1} height={310} width={340} overflow="auto" mb={1}>
              {dropdownScopeCollection.map((scope) => (
                <MenuItem
                  as={Checkbox}
                  py="4px"
                  key={scope.id}
                  label={scope.get('name')}
                  selected={scope.isSelected}
                  onClick={(e) => {
                    e.stopPropagation();
                    scope.select({ multi: true, shiftKey: e.shiftKey });
                  }}
                />
              ))}
            </Menu>
            <Flex p={1} justifyContent="space-between">
              <Button
                intent="primary"
                text="Apply"
                disabled={!dropdownScopeCollection.selectedSize}
                className="bp4-popover-dismiss"
                onClick={this.handleAdd}
              />
              <Button
                className="bp4-popover-dismiss"
                text="Cancel"
                mr={2}
                onClick={() => dropdownScopeCollection.clearSelection()}
              />
            </Flex>
          </Box>
        }
      >
        <Button ml={2} icon="plus" text="Add Global Filter" intent="primary" rightIcon="caret-down" ariaIsMenuButton />
      </DropdownMenu>
    );
  }

  get columns() {
    return [
      {
        name: 'name',
        label: 'Filter Name',
        flexBasis: 140
      },
      {
        name: 'dataSource',
        label: 'Data Source',
        flexBasis: 160,
        computed: true,
        renderer: ({ model }) => <Text>{model.dataSource}</Text>
      },
      {
        name: 'active_on_login',
        label: 'Logged-in State',
        flexBasis: 130,
        renderer: ({ model }) => {
          const isActive = model.get('active_on_login');

          if (!this.canEdit) {
            return isActive ? <Tag intent="success">Active on login</Tag> : <Text muted>&mdash;</Text>;
          }

          return (
            <Button
              small
              outlined={!isActive}
              icon={isActive ? 'tick' : undefined}
              intent={isActive ? 'success' : 'primary'}
              disabled={this.saving}
              text={isActive ? 'Active on login' : 'Set active'}
              onClick={() => this.handleToggleLoginDefault(model)}
            />
          );
        }
      },
      {
        type: CELL_TYPES.ACTION,
        width: 72,
        actions: [
          (model) => {
            if (!this.canEdit) {
              return <div key="remove" />;
            }

            return (
              <RemoveButton
                key="remove"
                model={model}
                showIcon
                hideText
                small
                popoverProps={{
                  confirmBodyContent: model.get('active_on_login')
                    ? 'This filter is active on login for this role set. Removing it leaves the role set with no filter applied at login.'
                    : 'Are you sure you want to remove this global filter?'
                }}
                onRemove={() => this.handleRemove(model.id)}
              />
            );
          }
        ]
      }
    ];
  }

  render() {
    const { model } = this.props;

    return (
      <Flex flexDirection="column" flex={1} px={2}>
        <Box mt={2} ml={1}>
          <Text as="div">
            <b>Note:</b> Global filter changes are applied immediately, regardless of save. Only one filter can be
            active on login per role set.
          </Text>
        </Box>

        <Flex mt={1} flexDirection="row" flex={1} style={{ overflow: 'auto' }}>
          <AdminTable
            tableContainerProps={{ bg: 'cardBackground' }}
            collection={model.scopes}
            flexed
            columns={this.columns}
            tableHeaderControlsPosition="afterSearch"
            tableHeaderControls={this.canEdit ? this.renderAddFilterDropdownMenu : null}
          />
        </Flex>
      </Flex>
    );
  }
}

export default RoleSetGlobalFilters;
